import * as Yup from "yup";
import { isValidTerminalId } from "../../../utils/terminalId";

const today = new Date().toISOString().split("T")[0];

export const merchantAndTerminalsInitialValues = {
  acquirer: {
    iso: "",
    isoID: "",
    currency: "",
    holding: "",
    holdingId: "",
    group: "",
    groupId: "",
    name: "",
    store: "",
    address: "",
    neighborhood: "",
    city: "",
    merchantState: "",
    postalCode: "",
    mccID: "",
    merchantID: "",
    amountOther: "",
    email: "",
    terminal: {
      terminals: [{ terminalID: "", terminalType: "", terminalSerial: "" }],
    },
    surcharge: "0",
  },
  merchant: {
    legal_name: "",
    business_email: "",
    business_phone: "",
    business_type: "",
    taxpayer_id: "",
    incorporation_date: today,
    effective_date: today,
    amount_tx_processing: "",
    amount_tx_processing_tiers: "0",
    percent_tx_processing: "",
    percent_tx_processing_tiers: "0",
    amount_terminal_maintenance: "0",
    amount_terminal_maintenance_tiers: "0",
    terminal_maintenance_inactive: "False",
    amount_terminal_setup: "0",
    amount_terminal_setup_tiers: "0",
  },
  bank: {
    bid: "",
    swift_code: "",
    account_number: "",
    address: "",
    routing_number: "",
    iban: "",
    branch_number: "",
    city: "",
    state: "",
    country_code: "",
    postal_code: "",
  },
  owner: [
    {
      first_name: "",
      last_name: "",
      middle_name: "",
      id: "",
      taxpayer_id: "",
      percentage: "0",
      id_issuer: "",
      personal_email: "",
      personal_phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      neighborhood: "",
      residency_time: "00",
    },
  ],
};

// const accountNumberName = (currency = "USD") => {
const accountNumberName = (currency) => {
  if (currency === "MXN") {
    return "CLABE";
  }
  return "Account number";
};

const createTerminalIdSchema = (formType) => {
  if (formType === "editTerminal" || formType === "seeTerminal") {
    return Yup.string()
      .optional()
      .max(64, "Terminal ID cannot exceed 64 characters");
  }

  return Yup.string()
    .required("Terminal ID is required")
    .max(64, "Terminal ID cannot exceed 64 characters")
    .test(
      "is-uuid",
      "Terminal ID must be a valid UUID",
      (value) => isValidTerminalId(value)
    );
};

export const createMerchantAndTerminalsValidationSchema = (
  formType,
  currency
) =>
  Yup.object().shape({
    // Acquirer Info Step
    acquirer: Yup.object().shape({
      iso: Yup.string().required("ISO selection is required"),
      isoID: Yup.string().when("iso", {
        is: "True",
        then: (schema) =>
          schema.required("ISO ID is required when ISO is selected"),
        otherwise: (schema) => schema.optional(),
      }),
      currency: Yup.string().required("Currency is required"),
      holding:
        formType === "editTerminal" || formType === "seeTerminal"
          ? Yup.string().required("Holding is required")
          : Yup.string().optional(),
      holdingId:
        formType === "editTerminal" || formType === "seeTerminal"
          ? Yup.string().required("Holding ID is required")
          : Yup.string().optional(),
      name: Yup.string()
        .required("Merchant name is required")
        .min(2, "Name must be at least 2 characters")
        .max(16, "Name cannot exceed 16 characters"),
      address: Yup.string()
        .required("Address is required")
        .min(5, "Address must be at least 5 characters")
        .max(200, "Address cannot exceed 200 characters"),
      city: Yup.string()
        .required("City is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters"),
      merchantState: Yup.string()
        .required("State is required")
        .min(2, "State must be at least 2 characters")
        .max(30, "State cannot exceed 30 characters"),
      postalCode: Yup.string()
        .required("Postal code is required")
        .min(3, "Postal code must be at least 3 characters")
        .max(20, "Postal code cannot exceed 20 characters"),
      mccID: Yup.string()
        .required("MCC is required")
        .min(4, "MCC must be 4 characters")
        .max(4, "MCC cannot exceed 4 characters"),
      merchantID: Yup.string()
        .required("Merchant ID is required")
        .min(4, "Merchant ID must be at least 4 characters")
        .max(64, "Merchant ID cannot exceed 64 characters"),
      email:
        formType === "editTerminal" || formType === "seeTerminal"
          ? Yup.string().optional()
          : Yup.string()
              .required("Email is required")
              .email("Invalid email format"),
      terminal: Yup.object().shape({
        terminals: Yup.array().of(
          Yup.object().shape({
            terminalID: createTerminalIdSchema(formType),
            terminalType: Yup.string()
              .required("Terminal type is required")
              // .min(2, "Terminal type must be at least 2 characters")
              .max(20, "Terminal type cannot exceed 20 characters"),
            terminalSerial: Yup.string()
              .required("Terminal serial is required")
              .min(4, "Terminal serial must be at least 4 characters")
              .max(30, "Terminal serial cannot exceed 30 characters"),
          })
        ),
      }),
      surcharge: Yup.number()
        .typeError("Surcharge must be a number")
        .required("Surcharge is required")
        .min(0, "Surcharge must be 0 or greater")
        .test(
          "numeric-5-2",
          "Surcharge must have at most 5 integer digits and 2 decimal places (e.g., 0, 0.00, 1.23, 12345.67, 99999.99)",
          (value) => {
            if (value === null || value === undefined) return false;

            // Convert to string to check format
            const valueStr = value.toString();

            // Split into integer and decimal parts
            const parts = valueStr.split(".");
            const integerPart = parts[0];
            const decimalPart = parts[1] || "";

            // Check integer part: maximum 5 digits
            if (integerPart.length > 5) {
              return false;
            }

            // Check decimal part: maximum 2 digits
            if (decimalPart.length > 2) {
              return false;
            }

            // Check total value doesn't exceed numeric(5,2) max: 99999.99
            if (value > 99999.99) {
              return false;
            }

            return true;
          }
        ),
    }),

    // Merchant Commissions Step
    merchant: Yup.object().shape({
      amount_tx_processing: Yup.number()
        .typeError("Processing Transaction Fee must be a number")
        .required("Processing Transaction Fee is required")
        .min(0, "Processing Transaction Fee must be 0 or greater")
        .test(
          "decimal-places",
          "Amount must be an integer or have at most 2 decimal places (e.g., 0, 0.00, 1, 1.0, 1.00, 0.10)",
          (value) => {
            if (value === null || value === undefined) return false;

            const valueStr = value.toString();

            // Si no hay punto decimal → es entero → válido
            if (!valueStr.includes(".")) {
              return true;
            }

            // Si hay punto decimal → debe tener máximo 2 decimales
            const decimalPlaces = valueStr.split(".")[1];
            const isValid = decimalPlaces && decimalPlaces.length <= 2;

            return isValid;
          }
        ),
      percent_tx_processing: Yup.number()
        .typeError("MDR Percentage Fee must be a number")
        .required("MDR Percentage Fee is required")
        .min(0, "MDR Percentage Fee must be 0 or greater")
        .test(
          "decimal-places",
          "Percentage must be an integer or have at most 4 decimal places (e.g., 0, 0.0, 0.00, 20, 20.0, 20.00, 20.000, 20.0000, 0.5000)",
          (value) => {
            if (value === null || value === undefined) return false;

            const valueStr = value.toString();

            // Si no hay punto decimal → es entero → válido
            if (!valueStr.includes(".")) {
              return true;
            }

            // Si hay punto decimal → debe tener máximo 4 decimales
            const decimalPlaces = valueStr.split(".")[1];
            const isValid = decimalPlaces && decimalPlaces.length <= 4;

            return isValid;
          }
        ),
      business_email: Yup.string()
        .required("Business email is required")
        .email("Invalid email format"),
      business_phone: Yup.string()
        .required("Business phone is required")
        .min(10, "Business phone must be at least 10 characters")
        .max(16, "Business phone cannot exceed 16 characters"),
      business_type: Yup.string()
        .required("Business type is required")
        .min(2, "Business type must be at least 2 characters")
        .max(50, "Business type cannot exceed 50 characters"),
      taxpayer_id: Yup.string()
        // .required("Taxpayer ID is required")
        .min(8, "Taxpayer ID must be at least 8 characters")
        .max(20, "Taxpayer ID cannot exceed 20 characters"),
      legal_name: Yup.string()
        .required("Legal name is required")
        .max(32, "Legal name cannot exceed 32 characters"),
    }),

    // Bank Info Step
    bank: Yup.object().shape({
      account_number: Yup.string()
        .required(`${accountNumberName(currency)} is required`)
        // .min(5, `${accountNumberName(currency)} must be at least 5 characters`)
        .max(18, `${accountNumberName(currency)} cannot exceed 18 characters`),
      address: Yup.string()
        .required("Bank address is required")
        .min(5, "Address must be at least 5 characters")
        .max(32, "Address cannot exceed 32 characters"),
      routing_number:
        currency === "MXN"
          ? Yup.string()
              .optional()
              // .length(9, "Routing number must be exactly 9 digits")
              // .matches(/^\d{9}$/, "Routing number must contain only digits"),
              .max(12, "Routing number cannot exceed 12 characters")
          : Yup.string()
              .required("Routing number is required")
              // .length(9, "Routing number must be exactly 9 digits")
              // .matches(/^\d{9}$/, "Routing number must contain only digits"),
              .max(12, "Routing number cannot exceed 12 characters"),
      Iban: Yup.string()
        .required("IBAN is required")
        .min(4, "IBAN must be at least 4 characters")
        .max(40, "IBAN cannot exceed 40 characters"),
    }),

    // Owners Info Step
    owner: Yup.array()
      .of(
        Yup.object().shape({
          first_name: Yup.string()
            .required("First name is required")
            .min(2, "First name must be at least 2 characters")
            .max(50, "First name cannot exceed 50 characters"),
          last_name: Yup.string()
            .required("Last name is required")
            .min(2, "Last name must be at least 2 characters")
            .max(50, "Last name cannot exceed 50 characters"),
          personal_email: Yup.string()
            .required("Personal email is required")
            .email("Invalid email format"),
          personal_phone: Yup.string()
            .required("Personal phone is required")
            .min(10, "Personal phone must be at least 10 characters")
            .max(16, "Personal phone cannot exceed 16 characters"),
          state: Yup.string()
            .required("State is required")
            // .min(2, "State must be at least 2 characters")
            .max(2, "State cannot exceed 2 characters"),
          country: Yup.string()
            .required("Country is required")
            // .min(2, "Country must be at least 2 characters")
            .max(2, "Country cannot exceed 2 characters"),
          taxpayer_id: Yup.string()
            // .required("Taxpayer ID is required")
            .min(8, "Taxpayer ID must be at least 8 characters")
            .max(20, "Taxpayer ID cannot exceed 20 characters"),
          id: Yup.string()
            .required("ID is required")
            .min(5, "ID must be at least 5 characters")
            .max(20, "ID cannot exceed 20 characters"),
          address: Yup.string()
            .required("Address is required")
            .min(5, "Address must be at least 5 characters")
            .max(200, "Address cannot exceed 200 characters"),
          city: Yup.string()
            .required("City is required")
            .min(2, "City must be at least 2 characters")
            .max(50, "City cannot exceed 50 characters"),
          postal_code: Yup.string()
            .required("Postal code is required")
            .min(3, "Postal code must be at least 3 characters")
            .max(20, "Postal code cannot exceed 20 characters"),
        })
      )
      .min(1, "At least one owner is required"),
  });

export const createShortMerchantAndTerminalsValidationSchema = (formType) =>
  Yup.object().shape({
    // Acquirer Info Step
    acquirer: Yup.object().shape({
      iso: Yup.string().required("ISO selection is required"),
      isoID: Yup.string().when("iso", {
        is: "True",
        then: (schema) =>
          schema.required("ISO ID is required when ISO is selected"),
        otherwise: (schema) => schema.optional(),
      }),
      currency: Yup.string().required("Currency is required"),
      holding:
        formType === "editTerminal" || formType === "seeTerminal"
          ? Yup.string().required("Holding is required")
          : Yup.string().optional(),
      holdingId:
        formType === "editTerminal" || formType === "seeTerminal"
          ? Yup.string().required("Holding ID is required")
          : Yup.string().optional(),
      name: Yup.string()
        .required("Merchant name is required")
        .min(2, "Name must be at least 2 characters")
        .max(16, "Name cannot exceed 16 characters"),
      address: Yup.string()
        .required("Address is required")
        .min(5, "Address must be at least 5 characters")
        .max(200, "Address cannot exceed 200 characters"),
      city: Yup.string()
        .required("City is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters"),
      merchantState: Yup.string()
        .required("State is required")
        .min(2, "State must be at least 2 characters")
        .max(30, "State cannot exceed 30 characters"),
      postalCode: Yup.string()
        .required("Postal code is required")
        .min(3, "Postal code must be at least 3 characters")
        .max(20, "Postal code cannot exceed 20 characters"),
      mccID: Yup.string()
        .required("MCC is required")
        .min(4, "MCC must be 4 characters")
        .max(4, "MCC cannot exceed 4 characters"),
      merchantID: Yup.string()
        .required("Merchant ID is required")
        .min(4, "Merchant ID must be at least 4 characters")
        .max(64, "Merchant ID cannot exceed 64 characters"),
      email:
        formType === "editTerminal" || formType === "seeTerminal"
          ? Yup.string().optional()
          : Yup.string()
              .required("Email is required")
              .email("Invalid email format"),
      terminal: Yup.object().shape({
        terminals: Yup.array().of(
          Yup.object().shape({
            terminalID: createTerminalIdSchema(formType),
            terminalType: Yup.string()
              .required("Terminal type is required")
              // .min(2, "Terminal type must be at least 2 characters")
              .max(20, "Terminal type cannot exceed 20 characters"),
            terminalSerial: Yup.string()
              .required("Terminal serial is required")
              .min(4, "Terminal serial must be at least 4 characters")
              .max(30, "Terminal serial cannot exceed 30 characters"),
          })
        ),
      }),
    }),

    // Merchant Commissions Step
    // merchant: Yup.object().shape({
    //   business_email: Yup.string()
    //     .required("Business email is required")
    //     .email("Invalid email format"),
    //   business_phone: Yup.string()
    //     .required("Business phone is required")
    //     .min(10, "Business phone must be at least 10 characters")
    //     .max(16, "Business phone cannot exceed 16 characters"),
    //   business_type: Yup.string()
    //     .required("Business type is required")
    //     .min(2, "Business type must be at least 2 characters")
    //     .max(50, "Business type cannot exceed 50 characters"),
    //   taxpayer_id: Yup.string()
    //     // .required("Taxpayer ID is required")
    //     .min(8, "Taxpayer ID must be at least 8 characters")
    //     .max(20, "Taxpayer ID cannot exceed 20 characters"),
    //   legal_name: Yup.string().required("Legal name is required"),
    // }),
  });

export const setMerchantAndTerminalsValues = ({
  formData,
  setFieldValue,
  formType,
}) => {
  // Set acquirer fields
  if (formData.acquirer) {
    Object.keys(formData.acquirer).forEach((key) => {
      if (key === "terminal") {
        // Handle terminal array
        if (formData.acquirer.terminal?.terminals) {
          formData.acquirer.terminal.terminals.forEach((terminal, index) => {
            setFieldValue(
              `acquirer.terminal.terminals.${index}.terminalID`,
              terminal.terminalID || "",
              true
            );
            setFieldValue(
              `acquirer.terminal.terminals.${index}.terminalType`,
              terminal.terminalType || "",
              true
            );
            setFieldValue(
              `acquirer.terminal.terminals.${index}.terminalSerial`,
              terminal.terminalSerial || "",
              true
            );
          });
        }
      } else if (key === "email" && formType === "editTerminal") {
        // No establecer email en editTerminal
      } else {
        setFieldValue(`acquirer.${key}`, formData.acquirer[key] || "", true);
      }
    });
  }

  // Set merchant fields
  if (formData.merchant) {
    Object.keys(formData.merchant).forEach((key) => {
      setFieldValue(`merchant.${key}`, formData.merchant[key] || "", true);
    });
  }

  // Set bank fields
  if (formData.bank) {
    Object.keys(formData.bank).forEach((key) => {
      setFieldValue(`bank.${key}`, formData.bank[key] || "", true);
    });
  }

  // Set owner fields
  if (formData.owner && Array.isArray(formData.owner)) {
    formData.owner.forEach((owner, index) => {
      Object.keys(owner).forEach((key) => {
        setFieldValue(`owner.${index}.${key}`, owner[key] || "", true);
      });
    });
  }
};

// Define step fields for validation
export const stepFields = {
  "Merchant Info": [
    "acquirer.iso",
    "acquirer.isoID",
    "acquirer.currency",
    "acquirer.name",
    "acquirer.address",
    "acquirer.merchantID",
    "acquirer.mccID",
    "acquirer.city",
    "acquirer.merchantState",
    "acquirer.postalCode",
    "acquirer.holding",
    "acquirer.holdingId",
    "acquirer.email",
  ],
  "Merchant Commissions": [
    "merchant.amount_tx_processing",
    "merchant.percent_tx_processing",
    "merchant.surcharge",
  ],
  "Bank Info": [
    "bank.account_number",
    "bank.address",
    "bank.routing_number",
    "bank.Iban",
  ],
  "Owners Info": ["owner"],
  Terminals: ["acquirer.terminal.terminals"],
};
